import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router";
import {
  Plus,
  Edit2,
  Trash2,
  Search,
  Layers,
  ArrowLeft,
  Loader,
} from "lucide-react";
import {
  listServices,
  createService,
  updateService,
  deleteService,
  getServiceById,
  addServiceComponent,
  removeServiceComponent,
  linkAssetToService,
  removeServiceAsset,
  createAssetDeployment,
  updateAssetDeployment,
  ServiceResponse,
  ServiceComponentMapping,
  ServiceComponentDeployment,
  ServiceStatus,
  listOrgUnits,
  OrgUnit,
  listCiDevices,
  DeviceResponse,
  listCloudProviders,
  CloudProviderResponse,
  listCloudProviderResources,
  listCloudProviderResourceTypes,
  AwsResourceListResponse,
  AwsServiceTypeResponse,
  AssetProtocol,
  AssetRole,
  AssetStatus,
  listCMDBComponents,
  CMDBComponent,
} from "../../api/frameworksApi";
import { useToastStore } from "../../stores/toastStore";
import {
  RBACButton,
  RBACAction,
  RBACMultiAction,
} from "../components/RBACButton";
import { DataTable, Column } from "../components/DataTable";
import { Tip } from "../components/ui/tooltip";
import {
  Modal,
  FormField,
  FormInput,
  FormSelect,
  FormTextarea,
  ModalFooterButtons,
} from "../components/Modal";
import { DeleteConfirmDialog } from "../components/DeleteConfirmDialog";
import {
  StatusBadge,
  autoFillPort,
  parseOptionalPort,
  flattenOrgHierarchy,
} from "./cmdb/cmdbUtils";
import {
  InfraPicker,
  DeviceForm,
  InfraTargetType,
} from "./cmdb/components/InfraPicker";

// ─── Constants ────────────────────────────────────────────────────────────────

const SERVICE_STATUS_OPTIONS: { value: ServiceStatus; label: string }[] = [
  { value: "ACTIVE", label: "Active" },
  { value: "INACTIVE", label: "Inactive" },
  { value: "DEGRADED", label: "Degraded" },
  { value: "MAINTENANCE", label: "Maintenance" },
];

const ROLE_OPTIONS: { value: AssetRole; label: string }[] = [
  { value: "PRIMARY", label: "Primary" },
  { value: "BACKUP", label: "Backup" },
  { value: "STANDBY", label: "Standby" },
  { value: "READ_REPLICA", label: "Read Replica" },
  { value: "DR", label: "DR" },
  { value: "ACTIVE", label: "Active" },
  { value: "PASSIVE", label: "Passive" },
];

const PROTOCOL_OPTIONS = [
  { value: "TCP", label: "TCP" },
  { value: "UDP", label: "UDP" },
  { value: "HTTP", label: "HTTP" },
  { value: "HTTPS", label: "HTTPS" },
  { value: "GRPC", label: "gRPC" },
];

const DEP_STATUS_OPTIONS: { value: AssetStatus; label: string }[] = [
  { value: "RUNNING", label: "Running" },
  { value: "STOPPED", label: "Stopped" },
  { value: "STARTING", label: "Starting" },
  { value: "DEGRADED", label: "Degraded" },
  { value: "MAINTENANCE", label: "Maintenance" },
  { value: "UNKNOWN", label: "Unknown" },
];

// ─── Form types ───────────────────────────────────────────────────────────────

interface ServiceForm {
  orgId: string;
  name: string;
  status: ServiceStatus;
  description: string;
}

const defaultServiceForm: ServiceForm = {
  orgId: "",
  name: "",
  status: "ACTIVE",
  description: "",
};

type WizardStep = "pick-component" | "add-device";

const defaultDeviceForm: DeviceForm = {
  targetType: "DEVICE",
  deviceId: "",
  cloudProviderId: "",
  cloudResourceId: "",
  port: "",
  protocol: "TCP",
  role: "PRIMARY",
  status: "RUNNING",
};

// ─── Component ────────────────────────────────────────────────────────────────

export function CMDBServices() {
  const navigate = useNavigate();
  const [services, setServices] = useState<ServiceResponse[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [page, setPage] = useState(0);
  const pageSize = 10;

  // Dropdown data
  const [orgOptions, setOrgOptions] = useState<OrgUnit[]>([]);
  const [devices, setDevices] = useState<DeviceResponse[]>([]);
  const [cloudAccounts, setCloudAccounts] = useState<CloudProviderResponse[]>(
    [],
  );
  const [awsResources, setAwsResources] = useState<AwsResourceListResponse[]>(
    [],
  );
  const [loadingAwsRes, setLoadingAwsRes] = useState(false);
  const [awsResTypes, setAwsResTypes] = useState<AwsServiceTypeResponse[]>([]);
  const [awsResTypeFilter, setAwsResTypeFilter] = useState("");
  const [awsResSearch, setAwsResSearch] = useState("");

  // Service CRUD
  const [showServiceForm, setShowServiceForm] = useState(false);
  const [editingService, setEditingService] = useState<ServiceResponse | null>(
    null,
  );
  const [serviceForm, setServiceForm] =
    useState<ServiceForm>(defaultServiceForm);
  const [isSavingService, setIsSavingService] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<ServiceResponse | null>(
    null,
  );
  const [isDeleting, setIsDeleting] = useState(false);

  // Add-component wizard
  const [wizardServiceId, setWizardServiceId] = useState<string | null>(null);
  const [wizardStep, setWizardStep] = useState<WizardStep>("pick-component");
  const [selectedComponent, setSelectedComponent] = useState<{
    id: string;
    title: string;
  } | null>(null);
  const [wizardComponentList, setWizardComponentList] = useState<
    CMDBComponent[]
  >([]);
  const [deviceForm, setDeviceForm] = useState<DeviceForm>(defaultDeviceForm);
  const [deviceSearch, setDeviceSearch] = useState("");
  const [isSavingWizard, setIsSavingWizard] = useState(false);
  const [showWizard, setShowWizard] = useState(false);

  // Add device to existing component
  const [addDeviceTarget, setAddDeviceTarget] = useState<{
    serviceId: string;
    comp: ServiceComponentMapping;
  } | null>(null);
  const [addDeviceForm, setAddDeviceForm] =
    useState<DeviceForm>(defaultDeviceForm);
  const [addDeviceSearch, setAddDeviceSearch] = useState<string>("");
  const [isSavingDevice, setIsSavingDevice] = useState(false);

  // Edit device row
  const [editTarget, setEditTarget] = useState<{
    dep: ServiceComponentDeployment;
    serviceId: string;
  } | null>(null);
  const [editForm, setEditForm] = useState({
    port: "",
    protocol: "TCP" as AssetProtocol,
    role: "PRIMARY" as AssetRole,
    status: "RUNNING" as AssetStatus,
  });
  const [isSavingEdit, setIsSavingEdit] = useState(false);

  // Deletion targets for components and assets
  const [deletingComponent, setDeletingComponent] = useState<{
    comp: ServiceComponentMapping;
    serviceId: string;
  } | null>(null);
  const [deletingAsset, setDeletingAsset] = useState<{
    dep: ServiceComponentDeployment;
    serviceId: string;
  } | null>(null);
  const [isRemovingCompId, setIsRemovingCompId] = useState<
    Record<string, boolean>
  >({});
  const [isRemovingAssetId, setIsRemovingAssetId] = useState<
    Record<string, boolean>
  >({});

  useEffect(() => {
    loadAll();
  }, []);

  async function loadAll() {
    setLoading(true);
    try {
      const [svcRes, orgRes, devRes, cloudRes] = await Promise.all([
        listServices().catch(() => ({ data: [] as ServiceResponse[] })),
        listOrgUnits().catch(() => ({ data: [] as OrgUnit[] })),
        listCiDevices().catch(() => ({ data: [] as DeviceResponse[] })),
        listCloudProviders().catch(() => ({
          data: [] as CloudProviderResponse[],
        })),
      ]);
      setServices(svcRes.data ?? []);
      setOrgOptions(flattenOrgHierarchy(orgRes.data ?? [], ["ORGANIZATION"]));
      setDevices(devRes.data ?? []);
      setCloudAccounts(cloudRes.data ?? []);
    } finally {
      setLoading(false);
    }
  }

  async function loadAwsResources(
    cloudProviderId: string,
    typeFilter?: string,
    q?: string,
  ) {
    setLoadingAwsRes(true);
    try {
      const res = await listCloudProviderResources(cloudProviderId, {
        cloudIdentifier: typeFilter || undefined,
        q: q || undefined,
      });
      setAwsResources(res.data ?? []);
    } catch {
      setAwsResources([]);
    } finally {
      setLoadingAwsRes(false);
    }
  }

  async function loadAwsResourceTypes(cloudProviderId: string) {
    try {
      const res = await listCloudProviderResourceTypes(cloudProviderId);
      setAwsResTypes(res.data ?? []);
    } catch {
      setAwsResTypes([]);
    }
  }

  async function reloadService(serviceId: string) {
    try {
      const res = await getServiceById(serviceId);
      setServices((prev) =>
        prev.map((s) => (s.id === serviceId ? res.data : s)),
      );
    } catch {
      /* ignore */
    }
  }

  // Reload cloud resources when type filter or search changes (debounced 300ms)
  const activeCloudProviderId =
    (deviceForm.targetType === "CLOUD" && deviceForm.cloudProviderId) ||
    (addDeviceForm.targetType === "CLOUD" && addDeviceForm.cloudProviderId) ||
    "";

  useEffect(() => {
    if (!activeCloudProviderId) return;
    const t = setTimeout(
      () =>
        loadAwsResources(activeCloudProviderId, awsResTypeFilter, awsResSearch),
      300,
    );
    return () => clearTimeout(t);
  }, [awsResTypeFilter, awsResSearch, activeCloudProviderId]);

  // ─── Service CRUD ─────────────────────────────────────────────────────────

  function openCreateService() {
    setEditingService(null);
    setServiceForm(defaultServiceForm);
    setShowServiceForm(true);
  }

  function openEditService(svc: ServiceResponse) {
    setEditingService(svc);
    setServiceForm({
      orgId: svc.orgId,
      name: svc.name,
      status: svc.status,
      description: svc.description ?? "",
    });
    setShowServiceForm(true);
  }

  async function handleSaveService() {
    if (!serviceForm.orgId) {
      useToastStore.getState().showError("Error", "Org unit is required");
      return;
    }
    if (!serviceForm.name.trim()) {
      useToastStore.getState().showError("Error", "Name is required");
      return;
    }
    setIsSavingService(true);
    try {
      const payload = {
        orgId: serviceForm.orgId,
        name: serviceForm.name.trim(),
        status: serviceForm.status,
        description: serviceForm.description || undefined,
      };
      if (editingService) {
        const res = await updateService(editingService.id, payload);
        setServices((prev) =>
          prev.map((s) => (s.id === editingService.id ? res.data : s)),
        );
        useToastStore.getState().showSuccess("Success", "Service updated");
      } else {
        const res = await createService(payload);
        setServices((prev) => [res.data, ...prev]);
        useToastStore.getState().showSuccess("Success", "Service created");
      }
      setShowServiceForm(false);
    } catch (err) {
      useToastStore
        .getState()
        .showError(
          "Error",
          err instanceof Error ? err.message : "Failed to save",
        );
    } finally {
      setIsSavingService(false);
    }
  }

  async function handleDeleteService() {
    if (!deleteTarget) return;
    setIsDeleting(true);
    try {
      await deleteService(deleteTarget.id);
      setServices((prev) => prev.filter((s) => s.id !== deleteTarget.id));
      useToastStore.getState().showSuccess("Success", "Service deleted");
      setDeleteTarget(null);
    } catch (err) {
      useToastStore
        .getState()
        .showError(
          "Error",
          err instanceof Error ? err.message : "Failed to delete",
        );
    } finally {
      setIsDeleting(false);
    }
  }

  // ─── Add-Component Wizard ─────────────────────────────────────────────────

  function openWizard(serviceId: string) {
    setWizardServiceId(serviceId);
    setWizardStep("pick-component");
    setSelectedComponent(null);
    setDeviceForm(defaultDeviceForm);
    setDeviceSearch("");
    setAwsResources([]);
    setAwsResTypes([]);
    setAwsResTypeFilter("");
    setAwsResSearch("");
    listCMDBComponents()
      .then((res) => setWizardComponentList(res.data || []))
      .catch(() => setWizardComponentList([]));
    setShowWizard(true);
  }

  function resetWizard() {
    setShowWizard(false);
    setWizardServiceId(null);
    setSelectedComponent(null);
  }

  async function handleWizardSave() {
    if (!wizardServiceId || !selectedComponent) return;
    const port = parseOptionalPort(deviceForm.port);
    if (port === undefined) {
      useToastStore
        .getState()
        .showError("Error", "Port must be a number between 1 and 65535");
      return;
    }

    setIsSavingWizard(true);
    try {
      // Step 1: link OSCAL component → service (409 = already linked, treat as OK)
      try {
        await addServiceComponent(wizardServiceId, {
          componentId: selectedComponent.id,
        });
      } catch (err: any) {
        if (!String(err.message).includes("409")) throw err;
      }

      // Step 2: if a target was picked, create asset + link to service
      const hasTarget =
        (deviceForm.targetType === "DEVICE" && deviceForm.deviceId) ||
        (deviceForm.targetType === "CLOUD" &&
          deviceForm.cloudProviderId &&
          deviceForm.cloudResourceId);

      if (hasTarget) {
        const assetPayload: any = {
          componentId: selectedComponent.id,
          port: port || undefined,
          protocol: deviceForm.protocol || undefined,
          role: deviceForm.role,
          status: deviceForm.status,
        };
        if (deviceForm.targetType === "DEVICE") {
          assetPayload.ciType = "ON_PREMISES";
          assetPayload.ciOnpremId = deviceForm.deviceId;
        } else {
          assetPayload.ciType = "CLOUD";
          assetPayload.ciCloudProviderId = deviceForm.cloudProviderId;
          assetPayload.cloudResourceId = deviceForm.cloudResourceId;
        }
        const assetRes = await createAssetDeployment(assetPayload);
        await linkAssetToService(wizardServiceId, assetRes.data.id);
      }

      useToastStore
        .getState()
        .showSuccess(
          "Success",
          hasTarget ? "Component and infrastructure added" : "Component added",
        );
      resetWizard();
      await reloadService(wizardServiceId);
    } catch (err) {
      useToastStore
        .getState()
        .showError(
          "Error",
          err instanceof Error ? err.message : "Failed to add component",
        );
    } finally {
      setIsSavingWizard(false);
    }
  }

  // ─── Add Device to existing component ────────────────────────────────────

  function openAddDevice(serviceId: string, comp: ServiceComponentMapping) {
    setAddDeviceTarget({ serviceId, comp });
    setAddDeviceForm(defaultDeviceForm);
    setAddDeviceSearch("");
    setAwsResources([]);
    setAwsResTypes([]);
    setAwsResTypeFilter("");
    setAwsResSearch("");
  }

  async function handleSaveAddDevice() {
    const f = addDeviceForm;
    const hasTarget =
      (f.targetType === "DEVICE" && f.deviceId) ||
      (f.targetType === "CLOUD" && f.cloudProviderId && f.cloudResourceId);
    if (!addDeviceTarget || !hasTarget) {
      useToastStore
        .getState()
        .showError(
          "Error",
          f.targetType === "DEVICE"
            ? "Select a device"
            : "Select a cloud account and resource",
        );
      return;
    }
    const port = parseOptionalPort(f.port);
    if (port === undefined) {
      useToastStore
        .getState()
        .showError("Error", "Port must be a number between 1 and 65535");
      return;
    }

    setIsSavingDevice(true);
    try {
      const assetPayload: any = {
        componentId: addDeviceTarget.comp.componentId,
        port: port || undefined,
        protocol: f.protocol || undefined,
        role: f.role,
        status: f.status,
      };
      if (f.targetType === "DEVICE") {
        assetPayload.ciType = "ON_PREMISES";
        assetPayload.ciOnpremId = f.deviceId;
      } else {
        assetPayload.ciType = "CLOUD";
        assetPayload.ciCloudProviderId = f.cloudProviderId;
        assetPayload.cloudResourceId = f.cloudResourceId;
      }
      const assetRes = await createAssetDeployment(assetPayload);
      await linkAssetToService(addDeviceTarget.serviceId, assetRes.data.id);
      useToastStore.getState().showSuccess("Success", "Infrastructure added");
      const sid = addDeviceTarget.serviceId;
      setAddDeviceTarget(null);
      await reloadService(sid);
    } catch (err) {
      useToastStore
        .getState()
        .showError(
          "Error",
          err instanceof Error ? err.message : "Failed to add infrastructure",
        );
    } finally {
      setIsSavingDevice(false);
    }
  }

  // ─── Remove component from service ───────────────────────────────────────

  async function confirmRemoveComponent() {
    if (!deletingComponent) return;
    const { comp, serviceId } = deletingComponent;
    setIsRemovingCompId((p) => ({ ...p, [comp.id]: true }));
    try {
      await removeServiceComponent(comp.id);
      await reloadService(serviceId);
      useToastStore.getState().showSuccess("Success", "Component removed");
      setDeletingComponent(null);
    } catch (err) {
      useToastStore
        .getState()
        .showError(
          "Error",
          err instanceof Error ? err.message : "Failed to remove component",
        );
    } finally {
      setIsRemovingCompId((p) => ({ ...p, [comp.id]: false }));
    }
  }

  // ─── Remove device from component ────────────────────────────────────────

  async function confirmRemoveDevice() {
    if (!deletingAsset) return;
    const { dep, serviceId } = deletingAsset;
    setIsRemovingAssetId((p) => ({ ...p, [dep.assetId]: true }));
    try {
      await removeServiceAsset(dep.assetId);
      await reloadService(serviceId);
      useToastStore.getState().showSuccess("Success", "Device removed");
      setDeletingAsset(null);
    } catch (err) {
      useToastStore
        .getState()
        .showError(
          "Error",
          err instanceof Error ? err.message : "Failed to remove device",
        );
    } finally {
      setIsRemovingAssetId((p) => ({ ...p, [dep.assetId]: false }));
    }
  }

  // ─── Edit device deployment ───────────────────────────────────────────────

  function openEditDevice(dep: ServiceComponentDeployment, serviceId: string) {
    setEditTarget({ dep, serviceId });
    setEditForm({
      port: dep.port != null ? String(dep.port) : "",
      protocol: (dep.protocol as AssetProtocol) ?? "TCP",
      role: (dep.role as AssetRole) ?? "PRIMARY",
      status: (dep.status as AssetStatus) ?? "RUNNING",
    });
  }

  async function handleSaveEdit() {
    if (!editTarget) return;
    const port = parseOptionalPort(editForm.port);
    if (port === undefined) {
      useToastStore
        .getState()
        .showError("Error", "Port must be a number between 1 and 65535");
      return;
    }

    setIsSavingEdit(true);
    try {
      await updateAssetDeployment(editTarget.dep.assetId, {
        port: port || null,
        protocol: editForm.protocol,
        role: editForm.role,
        status: editForm.status,
      });
      useToastStore.getState().showSuccess("Success", "Updated");
      await reloadService(editTarget.serviceId);
      setEditTarget(null);
    } catch (err) {
      useToastStore
        .getState()
        .showError(
          "Error",
          err instanceof Error ? err.message : "Failed to update",
        );
    } finally {
      setIsSavingEdit(false);
    }
  }

  // ─── Filter ───────────────────────────────────────────────────────────────

  const filtered = services.filter(
    (s) =>
      (s.name || "").toLowerCase().includes(searchQuery.toLowerCase()) ||
      (s.description || "").toLowerCase().includes(searchQuery.toLowerCase()),
  );

  useEffect(() => {
    setPage(0);
  }, [searchQuery]);

  function handleCloudAccountChange(providerId: string) {
    setAwsResTypeFilter("");
    if (providerId) {
      loadAwsResourceTypes(providerId);
      loadAwsResources(providerId);
    }
  }

  const columns: Column<ServiceResponse>[] = [
    {
      key: "_sno",
      header: "#",
      width: "w-10",
      align: "center",
      render: (_, i) => <span className="d text-sm">{i + 1}</span>,
    },
    {
      key: "name",
      header: "Name",
      align: "left",
      render: (row) => (
        <div className="flex items-center gap-2">
          <div className="flex-shrink-0 size-7 rounded-md flex items-center justify-center bg-purple-100 dark:bg-purple-500/20">
            <Layers className="size-3.5 text-purple-600 dark:text-purple-400" />
          </div>
          <span className="font-medium">{row.name}</span>
        </div>
      ),
    },
    {
      key: "description",
      header: "Description",
      align: "left",
      render: (row) => (
        <span className="text-sm d">{row.description || "—"}</span>
      ),
    },
    {
      key: "status",
      header: "Status",
      align: "center",
      render: (row) => <StatusBadge status={row.status} />,
    },
    {
      key: "components",
      header: "Components",
      align: "center",
      render: (row) => {
        const count = row.components?.length ?? 0;
        return (
          <span className="inline-flex items-center justify-center min-w-[1.5rem] px-2 py-0.5 rounded-full bg-indigo-100 text-indigo-700 dark:bg-indigo-500/20 dark:text-indigo-400 text-xs font-medium">
            {count}
          </span>
        );
      },
    },
    {
      key: "_actions",
      header: "Actions",
      align: "center",
      width: "w-28",
      render: (row) => (
        <div className="flex items-center justify-center gap-1">
          <RBACAction module="cmdb" action="update">
            <Tip label="Edit">
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  openEditService(row);
                }}
                className="p-1.5 hover:bg-muted rounded transition-colors"
              >
                <Edit2 className="size-4 d hover:text-foreground" />
              </button>
            </Tip>
          </RBACAction>
          <RBACAction module="cmdb" action="delete">
            <Tip label="Delete">
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  setDeleteTarget(row);
                }}
                className="p-1.5 hover:bg-red-50 dark:hover:bg-red-500/10 rounded transition-colors"
              >
                <Trash2 className="size-4 d hover:text-red-600" />
              </button>
            </Tip>
          </RBACAction>
        </div>
      ),
    },
  ];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-bold text-foreground">Services</h1>
          <p className="text-sm d mt-0.5">
            Manage shared services, clusters, and their underlying
            infrastructure.
          </p>
        </div>
        <div className="flex items-center gap-2 self-start sm:self-auto flex-shrink-0">
          <RBACMultiAction
            checks={[
              ["cmdb", "create"],
              ["cmdb", "update"],
            ]}
            type="any"
          >
            <button
              onClick={openCreateService}
              className="flex items-center gap-2 px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors"
            >
              <Plus className="size-4" />
              New Service
            </button>
          </RBACMultiAction>
        </div>
      </div>

      {/* Toolbar */}
      <div className="flex flex-wrap items-center gap-3">
        <div className="relative min-w-[200px] flex-1 max-w-xs">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 size-4 d" />
          <input
            type="text"
            placeholder="Search services"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full pl-10 pr-4 py-2 border border-border rounded-lg bg-background focus:border-indigo-500 outline-none text-sm"
          />
        </div>
        <span className="text-xs d ml-auto">{filtered.length} services</span>
      </div>

      <DataTable
        columns={columns}
        data={filtered.slice(page * pageSize, (page + 1) * pageSize)}
        loading={loading}
        emptyText="No services found. Create your first service."
        page={page}
        pageSize={pageSize}
        totalItems={filtered.length}
        onPageChange={setPage}
        onRowClick={(row) => navigate(`/cmdb/services/${row.id}`)}
      />

      {/* ── Service Create/Edit Modal ───────────────────────────────────────── */}
      <Modal
        open={showServiceForm}
        onClose={() => {
          if (!isSavingService) setShowServiceForm(false);
        }}
        title={editingService ? "Edit Service" : "New Service"}
        size="md"
        footer={
          <ModalFooterButtons
            onCancel={() => setShowServiceForm(false)}
            onSubmit={handleSaveService}
            submitLabel={isSavingService ? "Saving…" : "Save"}
            submitDisabled={isSavingService}
          />
        }
      >
        <div className="space-y-4">
          <FormField label="Org Unit" required>
            <FormSelect
              value={serviceForm.orgId}
              onChange={(v) => setServiceForm({ ...serviceForm, orgId: v })}
              options={[
                { value: "", label: "Select org unit…" },
                ...orgOptions.map((o) => ({ value: o.id, label: o.name })),
              ]}
            />
          </FormField>
          <FormField label="Name" required>
            <FormInput
              value={serviceForm.name}
              onChange={(v) => setServiceForm({ ...serviceForm, name: v })}
              placeholder="e.g. MySQL Cluster, Kafka Cluster"
            />
          </FormField>
          <FormField label="Status" required>
            <FormSelect
              value={serviceForm.status}
              onChange={(v) =>
                setServiceForm({ ...serviceForm, status: v as ServiceStatus })
              }
              options={SERVICE_STATUS_OPTIONS}
            />
          </FormField>
          <FormField label="Description">
            <FormTextarea
              value={serviceForm.description}
              onChange={(v) =>
                setServiceForm({ ...serviceForm, description: v })
              }
              placeholder="Optional description"
              rows={3}
            />
          </FormField>
        </div>
      </Modal>

      {/* ── Add Component Wizard ────────────────────────────────────────────── */}
      <Modal
        open={showWizard}
        onClose={resetWizard}
        title={
          wizardStep === "pick-component"
            ? "Add Component · Step 1 of 2: Pick Component"
            : "Add Component · Step 2 of 2: Add Device (optional)"
        }
        size="md"
      >
        <div className="space-y-4">
          {/* Step 1: Pick OSCAL component */}
          {wizardStep === "pick-component" && (
            <div className="space-y-4">
              <FormField label="Component" required>
                <FormSelect
                  value={selectedComponent?.id || ""}
                  onChange={(cid) => {
                    const c = wizardComponentList.find((x) => x.id === cid);
                    if (!c) return;
                    setSelectedComponent({ id: c.id, title: c.title });
                    setWizardStep("add-device");
                  }}
                  options={wizardComponentList.map((c) => ({
                    value: c.id,
                    label: c.componentType
                      ? `${c.title} (${c.componentType})`
                      : c.title,
                  }))}
                  placeholder="Select a component"
                />
              </FormField>
              <div className="flex justify-end pt-2 border-t border-border">
                <button
                  onClick={resetWizard}
                  className="px-4 py-2 border border-border rounded-lg hover:bg-muted transition-colors text-sm"
                >
                  Cancel
                </button>
              </div>
            </div>
          )}

          {/* Step 2: Add device (optional) */}
          {wizardStep === "add-device" && selectedComponent && (
            <div className="space-y-4">
              <div className="px-3 py-2 bg-muted/50 rounded-lg text-sm">
                Component:{" "}
                <span className="font-semibold">{selectedComponent.title}</span>
              </div>
              <InfraPicker
                search={deviceSearch}
                onSearch={setDeviceSearch}
                form={deviceForm}
                onForm={setDeviceForm}
                devices={devices}
                cloudAccounts={cloudAccounts}
                awsResTypes={awsResTypes}
                awsResTypeFilter={awsResTypeFilter}
                onResTypeFilter={setAwsResTypeFilter}
                awsResSearch={awsResSearch}
                onResSearch={setAwsResSearch}
                awsResources={awsResources}
                loadingAwsRes={loadingAwsRes}
                onCloudAccountChange={handleCloudAccountChange}
              />
              <p className="text-xs d">
                Infrastructure selection is optional — you can add it later from
                the component card.
              </p>
              <div className="flex justify-end gap-3 pt-2 border-t border-border">
                <button
                  onClick={resetWizard}
                  disabled={isSavingWizard}
                  className="px-4 py-2 border border-border rounded-lg hover:bg-muted transition-colors text-sm disabled:opacity-50"
                >
                  Cancel
                </button>
                <button
                  onClick={handleWizardSave}
                  disabled={isSavingWizard}
                  className="flex items-center gap-2 px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors text-sm disabled:opacity-50"
                >
                  {isSavingWizard && (
                    <Loader className="size-3.5 animate-spin" />
                  )}
                  Save
                </button>
              </div>
            </div>
          )}
        </div>
      </Modal>

      {/* ── Add Device to Existing Component Modal ──────────────────────────── */}
      <Modal
        open={!!addDeviceTarget}
        onClose={() => {
          if (!isSavingDevice) setAddDeviceTarget(null);
        }}
        title={`Add Device — ${addDeviceTarget?.comp.component.title ?? ""}`}
        size="md"
        footer={
          <ModalFooterButtons
            onCancel={() => setAddDeviceTarget(null)}
            onSubmit={handleSaveAddDevice}
            submitLabel={isSavingDevice ? "Saving…" : "Save"}
            submitDisabled={
              isSavingDevice ||
              (addDeviceForm.targetType === "DEVICE"
                ? !addDeviceForm.deviceId
                : !addDeviceForm.cloudResourceId)
            }
          />
        }
      >
        <InfraPicker
          search={addDeviceSearch}
          onSearch={setAddDeviceSearch}
          form={addDeviceForm}
          onForm={setAddDeviceForm}
          devices={devices}
          cloudAccounts={cloudAccounts}
          awsResTypes={awsResTypes}
          awsResTypeFilter={awsResTypeFilter}
          onResTypeFilter={setAwsResTypeFilter}
          awsResSearch={awsResSearch}
          onResSearch={setAwsResSearch}
          awsResources={awsResources}
          loadingAwsRes={loadingAwsRes}
          onCloudAccountChange={handleCloudAccountChange}
        />
      </Modal>

      {/* ── Edit Device Deployment Modal ────────────────────────────────────── */}
      <Modal
        open={!!editTarget}
        onClose={() => {
          if (!isSavingEdit) setEditTarget(null);
        }}
        title={`Edit — ${editTarget?.dep.ciName ?? ""}`}
        size="sm"
        footer={
          <ModalFooterButtons
            onCancel={() => setEditTarget(null)}
            onSubmit={handleSaveEdit}
            submitLabel={isSavingEdit ? "Saving…" : "Save"}
            submitDisabled={isSavingEdit}
          />
        }
      >
        {editTarget && (
          <div className="space-y-4">
            <div className="px-3 py-2 bg-muted/50 rounded-lg text-sm">
              <p className="font-medium">
                {editTarget.dep.ciType === "CLOUD"
                  ? editTarget.dep.awsResourceName ||
                    editTarget.dep.awsResourceIdentifier ||
                    editTarget.dep.ciName
                  : editTarget.dep.ciName}
              </p>
              <p className="text-xs d">
                {editTarget.dep.ciType === "CLOUD"
                  ? [
                      editTarget.dep.awsServiceName,
                      editTarget.dep.ciCloudProviderName,
                    ]
                      .filter(Boolean)
                      .join(" · ")
                  : editTarget.dep.ciIpAddress || editTarget.dep.ciIdentifier}
              </p>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <FormField label="Port">
                <FormInput
                  type="number"
                  value={editForm.port}
                  onChange={(v) => setEditForm({ ...editForm, port: v })}
                  placeholder="e.g. 3306"
                />
              </FormField>
              <FormField label="Role" required>
                <FormSelect
                  value={editForm.role}
                  onChange={(v) =>
                    setEditForm({ ...editForm, role: v as AssetRole })
                  }
                  options={ROLE_OPTIONS}
                />
              </FormField>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <FormField label="Protocol">
                <FormSelect
                  value={editForm.protocol}
                  onChange={(v) =>
                    setEditForm({ ...editForm, protocol: v as AssetProtocol })
                  }
                  options={PROTOCOL_OPTIONS}
                />
              </FormField>
              <FormField label="Status">
                <FormSelect
                  value={editForm.status}
                  onChange={(v) =>
                    setEditForm({ ...editForm, status: v as AssetStatus })
                  }
                  options={DEP_STATUS_OPTIONS}
                />
              </FormField>
            </div>
          </div>
        )}
      </Modal>

      {/* ── Delete Confirmation Dialogs ─────────────────────────────────────── */}
      <DeleteConfirmDialog
        open={!!deleteTarget}
        type="Service"
        name={deleteTarget?.name ?? ""}
        deleting={isDeleting}
        onCancel={() => setDeleteTarget(null)}
        onConfirm={handleDeleteService}
      />

      <DeleteConfirmDialog
        open={!!deletingComponent}
        type="Component"
        name={deletingComponent?.comp.component.title ?? ""}
        deleting={Object.values(isRemovingCompId).some(Boolean)}
        onCancel={() => setDeletingComponent(null)}
        onConfirm={confirmRemoveComponent}
      />

      <DeleteConfirmDialog
        open={!!deletingAsset}
        type="Infrastructure"
        name={deletingAsset?.dep.ciName ?? "this infrastructure"}
        deleting={Object.values(isRemovingAssetId).some(Boolean)}
        onCancel={() => setDeletingAsset(null)}
        onConfirm={confirmRemoveDevice}
      />
    </div>
  );
}
